/**
 * RF-10/RN-11 do PRD 32 — navegação por teclado na folha de monitoramento (US-06/J-04,
 * web#2118). Módulo puro: a lista de atalhos como dado (para o popover "Atalhos" da barra
 * de ações) e a única decisão de atalho que não é nativa do jspreadsheet CE nem já coberta
 * por hooks existentes (`handleBeforeChange`/`handleShortcutKeyDown` em
 * `AttendanceSheetCard.vue`) — `Shift+Espaço` seleciona a linha inteira.
 *
 * Vive no design system, não em `DsAttendance/utils/` — mesma razão de `sheetRowLimits.ts`
 * (ver seu comentário): quem consome a decisão de atalho é
 * `AttendanceSheetCard.vue` (`Components`); a lista de atalhos é consumida por
 * `DsAttendanceSheetActionBar.vue` (`Modules`), que PODE importar de `Components` — a
 * direção proibida é a contrária.
 *
 * A maior parte do RF-10 (setas, Enter, Tab, Esc, Ctrl+C/V, Ctrl+Z/Y, F2, Espaço em
 * checkbox, Shift+setas, Delete/Backspace) já é nativa do jspreadsheet CE ou já foi
 * coberta por hooks existentes — não reimplementada aqui. Ver o corpo do PR de #2118 para
 * o levantamento completo, feito lendo `keyDownControls` no bundle da lib.
 */

/** RF-10 — conjunto completo de atalhos, exibido no popover "Atalhos" da barra de ações. */
export interface SheetShortcutDescription {
  keys: string
  description: string
}

export const SHEET_SHORTCUTS: readonly SheetShortcutDescription[] = [
  { keys: 'Setas', description: 'Movem o foco entre células' },
  { keys: 'Enter', description: 'Confirma a edição e desce' },
  { keys: 'Tab', description: 'Confirma e avança' },
  {
    keys: 'Esc (1ª vez / 2ª vez)',
    description:
      '1ª fecha a lista de sugestões mantendo o texto; 2ª cancela a edição restaurando o valor anterior'
  },
  { keys: 'Ctrl+C / Ctrl+V', description: 'Copia / cola' },
  { keys: 'Ctrl+Z / Ctrl+Y', description: 'Desfaz / refaz' },
  { keys: 'Shift+Espaço', description: 'Seleciona a linha inteira' },
  {
    keys: 'Delete / Backspace',
    description: 'Limpa a seleção — em coluna de data, restaura a data sugerida'
  },
  { keys: 'F2', description: 'Inicia a edição da célula (alternativa ao Enter)' },
  { keys: 'Espaço (coluna de checkbox)', description: 'Marca/desmarca' },
  { keys: 'Shift + setas', description: 'Estende a seleção' }
]

/** Subconjunto de `KeyboardEvent` de que a decisão depende — testável sem DOM real. */
export type ShortcutKeyEvent = Pick<
  KeyboardEvent,
  'key' | 'code' | 'shiftKey' | 'ctrlKey' | 'metaKey' | 'altKey'
>

/**
 * RF-10 — `Shift+Espaço` seleciona a linha inteira. Único atalho novo desta Jornada que
 * não existe nativamente na lib: o `keyDownControls` dela trata `32==e.keyCode` (Espaço)
 * igual com ou sem `Shift` — marca/desmarca checkbox ou abre o editor substituindo o
 * conteúdo — nunca seleciona a linha. `Ctrl`/`Meta`/`Alt` desarmam para não colidir com um
 * atalho de navegador ou de sistema que também use Espaço.
 */
export function isSelectRowShortcut(event: ShortcutKeyEvent): boolean {
  const isSpace = event.code === 'Space' || event.key === ' '
  return isSpace && event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey
}
